<script setup>
/**
 * ItemDialog Component
 * Premium item dialog - desktop modal / mobile fullscreen
 * Uses CUMULATIVE promo calculation for accurate step/max display
 */
import { computed, ref, watch, toRef, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBackButtonClose } from '../../../../core/composables/useBackButtonClose.js'
import { useCatalogStore } from '../../../../app/store/catalog.js'
import { ROUTES } from '../../../../core/config/constants.js'
import { usePromotionsStore } from '../../../../app/store/promotions.js'
import { useAmountDiscountsStore } from '../../../../app/store/amountDiscounts.js'
import { useAuthStore } from '../../../../app/store/auth.js'
import ItemDialogHeaderImage from './ItemDialogHeaderImage.vue'
import ItemDialogDetails from './ItemDialogDetails.vue'
import ItemDialogActions from './actions/ItemDialogActions.vue'
import ItemFeatureExplainer from './ItemFeatureExplainer.vue'
import { getPromoBadgeType, parseBoxSize, getDisplayExpDate } from './itemDialogUtils.js'
import { useItemPricing } from '../../../../core/composables/useItemPricing.js'
import { useCloudFunctions } from '../../../../core/composables/useCloudFunctions.js'
import { useTutorial } from '../../../../core/tutorials/useTutorial.js'
import { isSeenByUser, markSeenByUser } from '../../../../core/tutorials/tutorialStorage.js'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  makat: {
    type: String,
    default: null,
  },
  fromPromotion: {
    type: Object, // { id: string, label: string } when coming from promotion detail page
    default: null,
  },
  fromQuantityDiscount: {
    type: Object, // { paritMerakez: string, label: string } when coming from quantity discount page
    default: null,
  },
})

const emit = defineEmits(['update:open'])

const router = useRouter()
const route = useRoute()

const catalogStore = useCatalogStore()
const promotionsStore = usePromotionsStore()
const amountDiscountsStore = useAmountDiscountsStore()
const authStore = useAuthStore()

const { callFunction } = useCloudFunctions()

// Stock state
const stockData = ref(null)
const stockLoading = ref(false)
const { isAdmin } = storeToRefs(authStore)
const showPrices = computed(() => authStore.isAuthenticated)

async function fetchStock(makat) {
  stockLoading.value = true
  stockData.value = null
  try {
    const result = await callFunction('priorityGetItemStock', { makat })
    stockData.value = result?.stock || []
  } catch (err) {
    console.error('fetchStock error:', err)
    stockData.value = []
  } finally {
    stockLoading.value = false
  }
}

// Get item from catalog store
const item = computed(() => {
  if (!props.makat) return null
  return catalogStore.itemsByMakat.get(props.makat) || null
})

const boxSizeText = computed(() => {
  const size = parseBoxSize(item.value)
  return size > 0 ? `${size} יח' במארז` : null
})

const displayExpDate = computed(() => getDisplayExpDate(item.value))

// Shared effective-pricing logic — same source of truth as ItemDialogActions
// and the immersive catalog, so the price box can never disagree with the
// actions row or the catalog card (includes the "anticipated step" rule for
// box-only items whose first step equals one box).
const { promoDisplayInfo: promoInfo, discountLadder, promoLadder } = useItemPricing(item)

// ── First-time guidance: fullscreen pricing explainer + dialog mini-tour ──
// When the dialog opens on an item with a madregot promo / quantity discount
// for the first time, a fullscreen slide explains the mechanism; afterwards
// (or right away for regular items) the dialog's tooltip tour runs once.
const { startTutorialGroup } = useTutorial()
const explainerType = ref(null) // 'madregot' | 'discount' | null
let guideTimer = null

const EXPLAINER_SEEN_IDS = { madregot: 'explainer-madregot', discount: 'explainer-discount' }

// The item's actual ladder, rendered inside the explainer slide
const explainerTiers = computed(() => {
  if (explainerType.value === 'madregot') {
    return (promoLadder.value || []).map((s) => ({
      qty: `מ-${s.amount} יח'`,
      value: `₪${s.priceText} ליח'`,
    }))
  }
  if (explainerType.value === 'discount') {
    return (discountLadder.value || [])
      .filter((t) => !t.isBase)
      .map((t) => ({
        qty: `מ-${t.minQty} יח'`,
        value: `${t.discountPercent}%- · ₪${t.unitPriceAfterDiscount}`,
      }))
  }
  return []
})

function maybeStartItemGuide() {
  clearTimeout(guideTimer)
  // Let the dialog's enter animation settle before overlaying guidance
  guideTimer = setTimeout(() => {
    if (!props.open) return
    const uid = authStore.userId || 'anonymous'
    if (showPrices.value) {
      if (promoLadder.value?.length && !isSeenByUser(uid, EXPLAINER_SEEN_IDS.madregot)) {
        explainerType.value = 'madregot'
        return
      }
      if (discountLadder.value?.length && !isSeenByUser(uid, EXPLAINER_SEEN_IDS.discount)) {
        explainerType.value = 'discount'
        return
      }
    }
    startTutorialGroup('item-dialog-intro')
  }, 500)
}

function closeExplainer() {
  const seenId = EXPLAINER_SEEN_IDS[explainerType.value]
  if (seenId) markSeenByUser(authStore.userId || 'anonymous', seenId)
  explainerType.value = null
  startTutorialGroup('item-dialog-intro')
}

// Get department and subdepartment names
const departmentName = computed(() => {
  if (!item.value?.department) return null
  const dept = catalogStore.departments.find((d) => d.id === item.value.department)
  return dept?.name || null
})

const subDepartmentName = computed(() => {
  if (!item.value?.subDepartment) return null
  // Try to find in loaded subs
  for (const [, subs] of catalogStore.subsByDepartmentId) {
    const found = subs.find((s) => s.id === item.value.subDepartment)
    if (found) return found.name
  }
  return null
})

// Get badge type (promo or amount discount)
const promoBadgeType = computed(() => {
  if (!props.makat) return null
  // Use eligible promos only - excludes first_order if user not eligible
  const promos = promotionsStore.getEligiblePromosForMakat(props.makat)
  const promoBadge = getPromoBadgeType(promos)
  if (promoBadge) return promoBadge

  // If no promo, check for amount discounts
  if (item.value?.paritMerakez && amountDiscountsStore.hasAmountDiscount(item.value.paritMerakez)) {
    return 'green'
  }

  return null
})

// Close dialog
function close() {
  emit('update:open', false)
}

// Intercept back button to close dialog instead of navigating
useBackButtonClose(toRef(props, 'open'), close)

// Navigate back to promotion
function backToPromotion() {
  if (!props.fromPromotion) {
    close()
    return
  }

  emit('update:open', false)

  // Check if in agent mode
  const clientMakat = route.params.clientMakat

  if (clientMakat) {
    router.push({
      name: ROUTES.AGENTS_PROMOTION_DETAIL,
      params: {
        clientMakat,
        promoId: props.fromPromotion.id,
      },
    })
  } else {
    router.push({
      name: ROUTES.PROMOTION_DETAIL,
      params: {
        promoId: props.fromPromotion.id,
      },
    })
  }
}

// Navigate back to quantity discount page
function backToQuantityDiscount() {
  if (!props.fromQuantityDiscount) {
    close()
    return
  }

  emit('update:open', false)

  // Check if in agent mode
  const clientMakat = route.params.clientMakat

  if (clientMakat) {
    router.push({
      name: ROUTES.AGENTS_QUANTITY_DISCOUNT,
      params: {
        clientMakat,
        paritMerakez: props.fromQuantityDiscount.paritMerakez,
      },
    })
  } else {
    router.push({
      name: ROUTES.QUANTITY_DISCOUNT,
      params: {
        paritMerakez: props.fromQuantityDiscount.paritMerakez,
      },
    })
  }
}

// Handle overlay click
function handleOverlayClick(e) {
  if (e.target === e.currentTarget) {
    close()
  }
}

// Handle escape key
function handleKeydown(e) {
  if (e.key === 'Escape' && props.open) {
    close()
  }
}

// Prevent body scroll when open + fetch stock
watch(
  () => props.open,
  (open) => {
    if (open) {
      document.body.style.overflow = 'hidden'
      if (props.makat && authStore.isAuthenticated) fetchStock(props.makat)
      maybeStartItemGuide()
    } else {
      document.body.style.overflow = ''
      stockData.value = null
      stockLoading.value = false
      clearTimeout(guideTimer)
      explainerType.value = null
    }
  }
)

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
  clearTimeout(guideTimer)
})
</script>

<template>
  <Teleport to="body">
    <!-- Overlay -->
    <Transition name="overlay">
      <div v-if="open" class="dialog-overlay" @click="handleOverlayClick">
        <!-- Dialog Container -->
        <Transition name="dialog">
          <div v-if="open" class="dialog-container" role="dialog" aria-modal="true">
            <!-- Back to Promotion Button (when coming from promotion page) -->
            <button
              v-if="fromPromotion"
              class="close-btn back-to-promo"
              aria-label="חזרה למבצע"
              @click="backToPromotion"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span class="back-text">חזרה למבצע</span>
            </button>
            <!-- Back to Quantity Discount Button (when coming from quantity discount page) -->
            <button
              v-else-if="fromQuantityDiscount"
              class="close-btn back-to-discount"
              aria-label="חזרה להנחת כמות"
              @click="backToQuantityDiscount"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span class="back-text">חזרה להנחת כמות</span>
            </button>
            <!-- Regular Close Button -->
            <button v-else class="close-btn" aria-label="סגור" @click="close">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <!-- Header Image (fixed) -->
            <ItemDialogHeaderImage
              :image="item?.image"
              :item-name="item?.name"
              :badge-type="promoBadgeType"
              :box-size-text="boxSizeText"
              :exp-date="displayExpDate"
            />

            <!-- Primary Info (fixed) -->
            <div class="primary-section">
              <h2 class="item-name">
                {{ item?.name || 'פריט לא ידוע' }}
                <span v-if="item?.kosher_rabanut === '2'" class="kashlap-badge">כשל"פ</span>
              </h2>
              <div class="item-meta">
                <span class="item-makat">מק"ט: {{ item?.makat || '—' }}</span>
                <span v-if="boxSizeText" class="item-box-chip">{{ boxSizeText }}</span>
                <span v-if="item?.origin" class="item-origin">{{ item.origin }}</span>
                <span v-if="item?.kosher_type" class="item-kosher">
                  <img
                    v-if="item.kosher_rabanut === '1' || item.kosher_rabanut === '2'"
                    src="../../../../assets/utils/RabanutSign.png"
                    alt="באישור רבנות ראשית בלבד"
                    class="rabanut-badge"
                  />
                  בהשגחת {{ item.kosher_type }}
                </span>
                <span v-if="displayExpDate" class="item-exp-date">תוקף: {{ displayExpDate }}</span>
              </div>
            </div>

            <!-- Details (scrollable) -->
            <div class="dialog-content">
              <ItemDialogDetails
                :item="item"
                :department-name="departmentName"
                :sub-department-name="subDepartmentName"
                :promo-info="promoInfo"
                :discount-ladder="discountLadder"
                :stock-loading="stockLoading"
                :stock-data="stockData"
                :is-admin="isAdmin"
                :show-prices="showPrices"
              />
            </div>

            <!-- Actions (fixed) — hidden for guests -->
            <ItemDialogActions v-if="showPrices" :item="item" />
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- First-time pricing mechanism explainer (madregot / quantity discount) -->
    <Transition name="explainer">
      <ItemFeatureExplainer
        v-if="open && explainerType"
        :type="explainerType"
        :tiers="explainerTiers"
        @close="closeExplainer"
      />
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Explainer slide fade */
.explainer-enter-active {
  transition: opacity 0.3s ease;
}

.explainer-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.explainer-enter-from {
  opacity: 0;
}

.explainer-leave-to {
  opacity: 0;
  transform: scale(1.04);
}

/* Overlay */
.dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Dialog Container */
.dialog-container {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-enter-active,
.dialog-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

/* Close Button */
.close-btn {
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  border: none;
  border-radius: var(--border-radius-sm);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.7);
  transform: scale(1.05);
}

.close-btn:active {
  transform: scale(0.95);
}

/* Back to Promotion Button Variant */
.close-btn.back-to-promo {
  width: auto;
  padding: var(--spacing-xs) var(--spacing-sm);
  gap: var(--spacing-xs);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
}

.close-btn.back-to-promo .back-text {
  font-weight: var(--font-weight-medium);
}

/* Back to Quantity Discount Button Variant */
.close-btn.back-to-discount {
  width: auto;
  padding: var(--spacing-xs) var(--spacing-sm);
  gap: var(--spacing-xs);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: rgba(16, 185, 129, 0.9);
}

.close-btn.back-to-discount:hover {
  background: rgba(16, 185, 129, 1);
}

.close-btn.back-to-discount .back-text {
  font-weight: var(--font-weight-medium);
}

/* Primary Section (fixed) */
.primary-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-lg) var(--spacing-lg) var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
}

.item-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
}

.kashlap-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  background: linear-gradient(135deg, #0d9488, #0f766e);
  color: white;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  border-radius: 4px;
  letter-spacing: 0.3px;
  line-height: 1.3;
  margin-inline-start: var(--spacing-sm);
}

.item-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.item-makat {
  font-size: var(--font-size-base);
  color: white;
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  background: var(--color-primary);
  padding: 4px 12px;
  border-radius: var(--border-radius-sm);
}

.item-origin {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.item-kosher {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.rabanut-badge {
  width: 18px;
  height: auto;
  opacity: 0.9;
}

.item-exp-date {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Box-size chip — replaces the image overlay on the compact mobile header */
.item-box-chip {
  display: none;
  align-items: center;
  padding: 2px 8px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-sm);
  white-space: nowrap;
}

/* Dialog Content */
.dialog-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Desktop (PC): wide two-pane modal — content column at inline-start (right in RTL),
   full-height image pane at inline-end (left in RTL) */
@media (min-width: 1280px) {
  .dialog-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(380px, 42%);
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'primary media'
      'content media'
      'actions media';
    max-width: 1100px;
    width: 100%;
    height: min(720px, 90vh);
  }

  /* Image pane fills its column top-to-bottom */
  .dialog-container :deep(.header-image-container) {
    grid-area: media;
    height: 100%;
    border-inline-start: 1px solid var(--border-color);
  }

  .dialog-container :deep(.header-image) {
    padding: var(--spacing-2xl);
  }

  .primary-section {
    grid-area: primary;
    padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-md);
  }

  .item-name {
    font-size: var(--font-size-2xl);
  }

  .dialog-content {
    grid-area: content;
    min-height: 0;
  }

  .dialog-content :deep(.item-details) {
    padding: var(--spacing-lg) var(--spacing-xl);
  }

  .dialog-container > :deep(.dialog-actions) {
    grid-area: actions;
    padding: var(--spacing-lg) var(--spacing-xl);
  }
}

/* Mobile & Tablet: Fullscreen */
@media (max-width: 1279px) {
  .dialog-overlay {
    padding: 0;
    align-items: stretch;
  }

  .dialog-container {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }

  .item-meta {
    gap: var(--spacing-sm);
  }

  .close-btn {
    top: var(--spacing-sm);
    left: var(--spacing-sm);
    width: 36px;
    height: 36px;
  }

  .close-btn svg {
    width: 20px;
    height: 20px;
  }

  .item-name {
    font-size: var(--font-size-lg);
  }

  /* Adjust dialog animation for mobile & tablet */
  .dialog-enter-from,
  .dialog-leave-to {
    transform: translateY(100%);
  }
}

/* Tablet (mainly sales agents): fullscreen two-pane like desktop —
   big full-height product image beside a compact content column */
@media (min-width: 768px) and (max-width: 1279px) {
  .dialog-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(280px, 40%);
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'primary media'
      'content media'
      'actions media';
  }

  .dialog-container :deep(.header-image-container) {
    grid-area: media;
    height: 100%;
    border-inline-start: 1px solid var(--border-color);
  }

  .dialog-container :deep(.header-image) {
    padding: var(--spacing-xl);
  }

  .primary-section {
    grid-area: primary;
  }

  .dialog-content {
    grid-area: content;
    min-height: 0;
  }

  .dialog-container > :deep(.dialog-actions) {
    grid-area: actions;
  }
}

/* Phone: compact action-focused layout — thumbnail beside the title instead
   of a full-width hero image, so price + actions fit with minimal scrolling */
@media (max-width: 767px) {
  .dialog-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'primary media'
      'content content'
      'actions actions';
  }

  /* Image becomes a compact thumbnail at inline-end (left in RTL) */
  .dialog-container :deep(.header-image-container) {
    grid-area: media;
    align-self: center;
  }

  /* The wide "back to promo/discount" pill floats over the thumb's corner —
     push the thumb down so the product stays fully visible and tappable */
  .dialog-container:has(.back-to-promo) :deep(.header-image-container),
  .dialog-container:has(.back-to-discount) :deep(.header-image-container) {
    margin-top: 52px;
  }

  .primary-section {
    grid-area: primary;
    align-self: center;
    border-bottom: none;
    padding: var(--spacing-sm) var(--spacing-md);
    /* keep clear of the floating close/back button overlaying the thumb */
    min-height: 96px;
    justify-content: center;
  }

  .dialog-content {
    grid-area: content;
    border-top: 1px solid var(--border-color);
  }

  .dialog-container > :deep(.dialog-actions) {
    grid-area: actions;
  }

  .item-box-chip {
    display: inline-flex;
  }
}
</style>
